import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Icon } from 'grab-ui';
import styles from './scroller.less';

const Scroller = ({
  className,
  contentClassName,
  children,
  scrollRatio = 0.75,
}) => {
  const scrollerRef = useRef();
  const scrollerDom = scrollerRef.current;
  const [scrollPosition, setScrollPosition] = useState(0);

  // need scrolling?
  const { maxScrollPosition, scrollDistance } = useMemo(
    () => {
      if (scrollerDom) {
        const widthDiff = scrollerDom.scrollWidth - scrollerDom.clientWidth;
        if (widthDiff > 0) {
          return {
            maxScrollPosition: widthDiff,
            scrollDistance: scrollerDom.clientWidth * scrollRatio,
          };
        }
      }
      return { maxScrollPosition: 0, scrollDistance: 0 };
    },
    [scrollerDom && scrollerDom.scrollWidth, scrollRatio],
  );

  // detect scrolling position
  useEffect(
    () => {
      if (scrollerDom) {
        const listener = () => {
          setScrollPosition(scrollerDom.scrollLeft);
        };
        scrollerDom.addEventListener('scroll', listener);
        return () => {
          scrollerDom.removeEventListener('scroll', listener);
        };
      }
    },
    [scrollerDom],
  );

  // scroll button action
  const onScrollTo = useCallback(
    direction => () => {
      if (scrollerDom) {
        if (direction === 'left') {
          scrollerDom.scrollLeft -= scrollDistance;
        } else {
          scrollerDom.scrollLeft += scrollDistance;
        }
      }
    },
    [scrollerDom, scrollRatio, scrollDistance],
  );

  return (
    <div className={cx(styles.scroller, className)}>
      {maxScrollPosition > 0 && (
        <a
          className={cx(styles.scrollButton, styles.prev, {
            [styles.hide]: scrollPosition === 0,
          })}
          onClick={onScrollTo('left')}
        >
          <Icon type={'left'} />
        </a>
      )}
      <div
        className={cx(styles.scrollingContent, contentClassName)}
        ref={scrollerRef}
      >
        {children}
      </div>
      {maxScrollPosition > 0 && (
        <a
          className={cx(styles.scrollButton, styles.next, {
            [styles.hide]: scrollPosition >= maxScrollPosition - 1,
          })}
          onClick={onScrollTo('right')}
        >
          <Icon type={'right'} />
        </a>
      )}
    </div>
  );
};

Scroller.defaultProps = {
  className: undefined,
  contentClassName: undefined,
  scrollRatio: undefined,
};

Scroller.propTypes = {
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  children: PropTypes.node.isRequired,
  scrollRatio: PropTypes.number,
};

export default Scroller;
